import React, { useState, useEffect } from 'react';
import NewHomePage from './components/NewHomePage';
import AdminHomePage from './components/AdminHomePage';
import AdminPanel from './components/AdminPanel';
import ListingAdmin from './components/ListingAdmin';
import ColorAudit from './components/ColorAudit';
import ParallaxTest from './components/ParallaxTest';
import SceneBuilder from './components/SceneBuilder';
import FAQ from './components/FAQ';
import HeroFrameMachineTestPage from './pages/HeroFrameMachineTestPage';
import SvgPathAutoAssociation from './components/SvgPathAutoAssociation';
import ErrorPreview from './components/ErrorPreview';
import navigationService from './services/navigationService';
import { trackPageView } from './services/analyticsService';
import uiAssetPreloader from './services/uiAssetPreloader';

import './App.css';

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  
  // Global loading state - only show on initial app load
  const [isInitialAppLoad, setIsInitialAppLoad] = useState(true);
  const [hasLoadedData, setHasLoadedData] = useState(false);

  // Track page views when currentPage changes
  useEffect(() => {
    if (currentPage && hasLoadedData) {
      const pageTitle = getPageTitle(currentPage);
      const pagePath = getPagePath(currentPage);
      trackPageView(pageTitle, pagePath);
    }
  }, [currentPage, hasLoadedData]);

  // Helper function to get page titles for analytics
  const getPageTitle = (page) => {
    const titles = {
      'home': 'ClothedEye - Home',
      'admin-home': 'ClothedEye - Admin Home',
      'admin': 'ClothedEye - Admin Panel',
      'listing-admin': 'ClothedEye - Listing Admin',
      'color-audit': 'ClothedEye - Color Audit',
      'parallax-test': 'ClothedEye - Parallax Test',
      'scene-builder': 'ClothedEye - Scene Builder',
      'faq': 'ClothedEye - FAQ',
      'svg-path-auto-association': 'ClothedEye - SVG Path Auto Association',
      'hero-frame-machine-test': 'ClothedEye - Hero Frame Machine Test',
      'error-preview': 'ClothedEye - Error Preview (DEV ONLY)'
    };
    return titles[page] || 'ClothedEye';
  };

  // Helper function to get page paths for analytics
  const getPagePath = (page) => {
    const paths = {
      'home': '/',
      'admin-home': '/admin-home',
      'admin': '/admin',
      'listing-admin': '/listing-admin',
      'color-audit': '/color-audit',
      'parallax-test': '/parallax',
      'scene-builder': '/scene-builder',
      'faq': '/faq',
      'svg-path-auto-association': '/svg-path-auto-association',
      'hero-frame-machine-test': '/hero-frame-machine-test',
      'error-preview': '/error-preview'
    };
    return paths[page] || '/';
  };

  useEffect(() => {
    // Function to handle routing based on current path
    const handleRouting = () => {
      const path = window.location.pathname;
      if (path === '/admin-home') {
        setCurrentPage('admin-home');
      } else if (path === '/admin') {
        setCurrentPage('admin');
      } else if (path === '/listing-admin') {
        setCurrentPage('listing-admin');
      } else if (path === '/color-audit') {
        setCurrentPage('color-audit');
      } else if (path === '/parallax') {
        setCurrentPage('parallax-test');
      } else if (path === '/scene-builder') {
        setCurrentPage('scene-builder');
      } else if (path === '/faq') {
        setCurrentPage('faq');
      } else if (path === '/svg-path-auto-association') {
        setCurrentPage('svg-path-auto-association');
      } else if (path === '/hero-frame-machine-test') {
        setCurrentPage('hero-frame-machine-test');
      } else if (path === '/error-preview') {
        setCurrentPage('error-preview');
      } else {
        setCurrentPage('home');
      }
    };

    // Initial routing
    handleRouting();

    // Listen for navigation service changes
    const unsubscribe = navigationService.addListener((pageName) => {
      setCurrentPage(pageName);
    });

    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', handleRouting);

    // Cleanup
    return () => {
      unsubscribe();
      window.removeEventListener('popstate', handleRouting);
    };
  }, []);
  
  // Handle initial app data loading (only once)
  useEffect(() => {
    if (hasLoadedData) return; // Already loaded
    
    // Preload UI assets in background (non-blocking, won't delay app)
    uiAssetPreloader.preloadAllUIAssets().catch(error => {
      console.error('UI preload error (non-blocking):', error);
    });
    
    // Original delay - don't change this timing
    const timer = setTimeout(() => {
      setHasLoadedData(true);
      setIsInitialAppLoad(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [hasLoadedData]);

  // Render the appropriate page based on currentPage
  if (currentPage === 'admin-home') {
    return <AdminHomePage />;
  }

  if (currentPage === 'admin') {
    return <AdminPanel />;
  }

  if (currentPage === 'listing-admin') {
    return <ListingAdmin />;
  }

  if (currentPage === 'color-audit') {
    return <ColorAudit />;
  }

  if (currentPage === 'parallax-test') {
    return <ParallaxTest />;
  }

  if (currentPage === 'scene-builder') {
    // Check if we're in admin mode (local development or admin URL)
    const isAdminMode = window.location.hostname === 'localhost' || 
                       window.location.pathname.includes('admin') ||
                       window.location.search.includes('admin=true');
    
    return <SceneBuilder adminMode={isAdminMode} isInitialAppLoad={isInitialAppLoad} hasLoadedData={hasLoadedData} />;
  }

  if (currentPage === 'faq') {
    return <FAQ />;
  }

  if (currentPage === 'svg-path-auto-association') {
    return <SvgPathAutoAssociation />;
  }

  if (currentPage === 'hero-frame-machine-test') {
    return <HeroFrameMachineTestPage />;
  }

  if (currentPage === 'error-preview') {
    return <ErrorPreview />;
  }

  return <NewHomePage isInitialAppLoad={isInitialAppLoad} hasLoadedData={hasLoadedData} />;
}

export default App; 